import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOutreachOverview, type OutreachOverviewRow } from '../../lib/refonteApi';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const fmtD = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—');

const Kpi: React.FC<{ v: React.ReactNode; l: string; tone?: string }> = ({ v, l, tone }) => (
  <div className="bg-panel border border-line rounded-card p-4">
    <div className={`text-[24px] font-bold font-mono tabular-nums leading-none ${tone || ''}`}>{v}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{l}</div>
  </div>
);

// Vue interne GLOBALE des campagnes mail (Instantly) & LinkedIn (HeyReach), par client.
// Montre volume de campagnes, réponses et réponses positives. Clique un client → sa fiche.
const CampagnesPage: React.FC = () => {
  const nav = useNavigate();
  const [rows, setRows] = useState<OutreachOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [chan, setChan] = useState<'tous' | 'mail' | 'linkedin'>('tous');

  useEffect(() => {
    (async () => { setLoading(true); try { setRows(await getOutreachOverview()); } catch (e: any) { setErr(e.message); } setLoading(false); })();
  }, []);

  const filtered = useMemo(() => rows.filter(r =>
    (!q || r.clientName.toLowerCase().includes(q.toLowerCase())) &&
    (chan === 'tous' || (chan === 'mail' ? r.mailCampaigns + r.mailReplies > 0 : r.liCampaigns + r.liReplies > 0))
  ), [rows, q, chan]);

  const tot = useMemo(() => rows.reduce((a, r) => ({
    clients: a.clients + 1,
    camp: a.camp + r.mailCampaigns + r.liCampaigns,
    rep: a.rep + r.mailReplies + r.liReplies,
    pos: a.pos + r.mailPositives + r.liPositives,
  }), { clients: 0, camp: 0, rep: 0, pos: 0 }), [rows]);

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Campagnes mail & LinkedIn</h1>
        <span className="font-mono text-[11px] text-faint">{rows.length} clients actifs sur ces canaux</span>
      </div>
      <p className="text-dim text-[13px] mb-4">Toutes les campagnes mail (Instantly) et LinkedIn (HeyReach) par client, avec réponses et réponses positives. Clique un client pour sa fiche.</p>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}

      {loading ? <SkeletonRows count={8} /> : rows.length === 0 ? (
        <EmptyState title="Aucune campagne" hint="Aucune campagne mail / LinkedIn rattachée à un client pour l'instant." />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Kpi v={tot.clients} l="Clients sur ces canaux" />
            <Kpi v={tot.camp} l="Campagnes" />
            <Kpi v={tot.rep} l="Réponses" />
            <Kpi v={tot.pos} l="Réponses positives" tone="text-hook-rose" />
          </div>

          <div className="bg-panel border border-line rounded-card p-5">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un client…" className="rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] w-64" />
              {([['tous', 'Tous'], ['mail', '✉️ Mail'], ['linkedin', 'in LinkedIn']] as const).map(([v, l]) => (
                <button key={v} onClick={() => setChan(v)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${chan === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l}</button>
              ))}
              <span className="ml-auto font-mono text-[11px] text-faint">{filtered.length} affichés</span>
            </div>

            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-line-2 font-mono text-[9.5px] uppercase tracking-widest text-faint">
                    <th className="py-2 px-2 font-semibold">Client</th>
                    <th className="py-2 px-2 font-semibold text-right">✉️ Campagnes</th>
                    <th className="py-2 px-2 font-semibold text-right">✉️ Réponses</th>
                    <th className="py-2 px-2 font-semibold text-right">✉️ Positives</th>
                    <th className="py-2 px-2 font-semibold text-right">in Campagnes</th>
                    <th className="py-2 px-2 font-semibold text-right">in Réponses</th>
                    <th className="py-2 px-2 font-semibold text-right">in Positives</th>
                    <th className="py-2 px-2 font-semibold whitespace-nowrap hidden md:table-cell">Dern. réponse</th>
                    <th className="py-2 px-2 w-6"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtered.map(r => (
                    <tr key={r.clientId} onClick={() => nav(`/app/clients/${r.clientId}`)} className="cursor-pointer hover:bg-panel-2 transition-colors">
                      <td className="py-2.5 px-2">
                        <div className="text-[13px] font-semibold">{r.clientName}</div>
                        {r.statut && r.statut !== 'actif' && <div className="font-mono text-[9px] uppercase tracking-widest text-faint">{r.statut}</div>}
                      </td>
                      <td className="py-2.5 px-2 font-mono text-[12px] text-dim tabular-nums text-right">{r.mailCampaigns || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[12px] text-dim tabular-nums text-right">{r.mailReplies || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[12px] tabular-nums text-right font-semibold text-hook-rose">{r.mailPositives || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[12px] text-dim tabular-nums text-right">{r.liCampaigns || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[12px] text-dim tabular-nums text-right">{r.liReplies || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[12px] tabular-nums text-right font-semibold text-hook-rose">{r.liPositives || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[11.5px] text-dim tabular-nums whitespace-nowrap hidden md:table-cell">{fmtD(r.lastReplyAt)}</td>
                      <td className="py-2.5 px-2 font-mono text-[11px] text-faint text-right">›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CampagnesPage;
